// src/pages/JobDetails.jsx
import { useParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { toast } from "react-hot-toast";
import API from "../api/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import OverviewTab from "../components/jobDetails/OverviewTab";
import WellInfoTab from "../components/jobDetails/WellInfoTab";
import RunInfoTab from "../components/jobDetails/RunInfoTab";
import SurveyDetailsTab from "../components/jobDetails/SurveyDetailsTab";
import ReportsTab from "../components/jobDetails/ReportsTab";
import RunSelector from "../components/jobDetails/RunSelector";
import NewRunModal from "../components/jobDetails/NewRunModal";
import SurveyUploadModal from "../components/jobDetails/SurveyUploadModal";

export default function JobDetails() {
  const { jobId, projectId } = useParams();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "overview";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [project, setProject] = useState(null);
  const [jobInfo, setJobInfo] = useState(null);
  const [wellInfo, setWellInfo] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [isNewRunModalOpen, setIsNewRunModalOpen] = useState(false);
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [selectedSurveyRun, setSelectedSurveyRun] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({ inrun: null, outrun: null });
  const [hasCalculated, setHasCalculated] = useState({
    qualityCheck: false,
    surveyCalculation: false,
    comparison: false
  });
  const [uploadedRuns, setUploadedRuns] = useState(() => {
    const saved = localStorage.getItem(`uploadedRuns_${jobId}`);
    return saved ? JSON.parse(saved) : {};
  });

  const navigate = useNavigate();
  const currentRun = runs.find((r) => r.id === selectedRun);

  // Create an effect to save to localStorage
  useEffect(() => {
    localStorage.setItem(`uploadedRuns_${jobId}`, JSON.stringify(uploadedRuns));
  }, [uploadedRuns, jobId]);

  // Fetch all job information
  useEffect(() => {
    const fetchAllInfo = async () => {
      try {
        setLoading(true);
        const res = await API.get(`/jobs/${jobId}/details/`);
        setJobInfo(res.data);
        setWellInfo(res.data.well_info);
        setRuns(res.data.runs);
        setProject(res.data.project);

        if (res.data.runs.length > 0) {
          setSelectedRun(res.data.runs[0].id);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load job details");
      } finally {
        setLoading(false);
      }
    };

    fetchAllInfo();
  }, [jobId]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    navigate(`?tab=${key}`, { replace: true });
  };

  const handleRunChange = (runId) => {
    setSelectedRun(runId);
  };

  const handleQualityCheck = async () => {
    try {
      navigate(`/survey-center/${jobId}/${currentRun?.id}`);
      setHasCalculated(prev => ({ ...prev, qualityCheck: true }));
    } catch (error) {
      toast.error("Failed to calculate quality check");
    }
  };

  const handleSurveyCalculation = async () => {
    try {
      toast.success("Survey calculation completed!");
      setHasCalculated(prev => ({ ...prev, surveyCalculation: true }));
    } catch (error) {
      toast.error("Failed to calculate survey");
    }
  };

  const handleComparison = async () => {
    try {
      toast.success("Comparison completed!");
      setHasCalculated(prev => ({ ...prev, comparison: true }));
    } catch (error) {
      toast.error("Failed to perform comparison");
    }
  };

  const shouldEnableSurveyButtons = (runId) => {
    return uploadedRuns[runId] || hasCalculated.surveyCalculation;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-red-600 to-gray-700 rounded-2xl shadow-xl p-6 mb-8 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{jobInfo?.job_number || "Job Details"}</h1>
            <div className="flex flex-wrap gap-4">
              <div className="bg-gray-700/50 px-3 py-1 rounded-full text-sm"><span className="font-semibold">Project:</span> {project?.project_id}</div>
              <div className="bg-gray-700/50 px-3 py-1 rounded-full text-sm"><span className="font-semibold">Rig:</span> {jobInfo?.rig_number}</div>
              <div className="bg-gray-700/50 px-3 py-1 rounded-full text-sm"><span className="font-semibold">Well:</span> {jobInfo?.well_name || "N/A"}</div>
            </div>
          </div>
          <div className="mt-4 md:mt-0">
            <div className="text-right">
              <p className="text-sm opacity-80">Start Date</p>
              <p className="font-medium">{jobInfo?.start_date || "N/A"}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Navigation Tabs and Run Selector */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="flex flex-wrap border-b border-gray-200 dark:border-gray-700">
          {[
            { label: "Overview", key: "overview" },
            { label: "Well Info", key: "well-info" },
            { label: "Run Info", key: "run-info" },
            // { label: "Survey Details", key: "survey-details" },
            { label: "Reports", key: "reports" },
          ].map(({ label, key }) => (
            <button
              key={key}
              onClick={() => handleTabChange(key)}
              className={`
                px-4 py-2 font-medium text-sm md:text-base
                ${activeTab === key
                  ? "text-red-600 border-b-2 border-red-600"
                  : "text-gray-500 hover:text-gray-700"}
              `}
            >
              {label}
            </button>
          ))}
        </div>
        
        <RunSelector 
          runs={runs}
          selectedRun={selectedRun}
          onRunChange={handleRunChange}
          onNewRun={() => setIsNewRunModalOpen(true)}
        />
      </div>
      
      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-md p-6  dark:bg-gray-800">
        {activeTab === "overview" && (
          <OverviewTab 
            jobInfo={jobInfo}
            wellInfo={wellInfo}
            currentRun={currentRun}
            project={project}
          />
        )}
        
        {activeTab === "well-info" && (
          <WellInfoTab wellInfo={wellInfo} />
        )}
        
        {activeTab === "run-info" && (
          <RunInfoTab 
            currentRun={currentRun}
            jobId={jobId}
            wellInfo={wellInfo}
            onSurveyCalculation={() => {
              setSelectedSurveyRun(currentRun);
              setIsSurveyModalOpen(true);
            }}
            onQualityCheck={handleQualityCheck}
            onComparison={handleComparison}
            shouldEnableSurveyButtons={shouldEnableSurveyButtons}
            hasCalculated={hasCalculated}
          />
        )}
        
        {/* {activeTab === "survey-details" && (
          <SurveyDetailsTab 
            currentRun={currentRun}
            jobId={jobId}
            onQualityCheck={handleQualityCheck}
            onSurveyCalculation={() => {
              setSelectedSurveyRun(currentRun);
              setIsSurveyModalOpen(true);
            }}
            onComparison={handleComparison}
            shouldEnableSurveyButtons={shouldEnableSurveyButtons}
            hasCalculated={hasCalculated}
          />
        )} */}
        
        {activeTab === "reports" && (
          <ReportsTab 
            jobInfo={jobInfo}
            wellInfo={wellInfo}
            currentRun={currentRun}
          />
        )}
      </div>
      
      {/* Modals */}
      {isNewRunModalOpen && (
        <NewRunModal
          jobId={jobId}
          wellInfo={wellInfo}
          runs={runs}
          onClose={() => setIsNewRunModalOpen(false)}
          onRunCreated={(newRun) => {
            setRuns(prev => [...prev, newRun]);
            setSelectedRun(newRun.id);
          }}
        />
      )}
      
      {isSurveyModalOpen && selectedSurveyRun && (
  <SurveyUploadModal
    selectedSurveyRun={selectedSurveyRun}
    uploadedFiles={uploadedFiles}
    onFileUpload={setUploadedFiles}
    onClose={() => {
      setIsSurveyModalOpen(false);
      setUploadedFiles({ inrun: null, outrun: null });
    }}
    onSubmit={() => {
      setUploadedRuns(prev => ({
        ...prev,
        [selectedSurveyRun.id]: true
      }));
      setIsSurveyModalOpen(false);
      setUploadedFiles({ inrun: null, outrun: null });
      setHasCalculated(prev => ({ ...prev, surveyCalculation: true }));
    }}
    jobId={jobId} // This line is crucial
  />
)}
    </div>
  );
}